import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

interface UseSwipeablePagerOptions {
  tabCount: number;
  initialIndex?: number;
  onChange?: (index: number) => void;
}

interface UseSwipeablePagerResult {
  activeIndex: number;
  /** Навесить на контейнер со scroll-snap. */
  containerRef: RefObject<HTMLDivElement>;
  /** Программный переход (тап по таббару). */
  goTo: (index: number) => void;
}

/**
 * Горизонтальный пейджер на CSS Scroll Snap.
 *
 * Почему не Swiper и не Framer Motion: нативный скролл в вебвью Telegram даёт
 * инерцию и «резинку» самой платформы, а JS-библиотеки её эмулируют — на
 * Android это заметно по рассинхрону с жестом. Scroll Snap стоит ноль килобайт,
 * работает на композиторе и не конфликтует с вертикальными списками внутри вкладок.
 *
 * Хук решает единственную сложность такого подхода: синхронизацию позиции
 * скролла с активным индексом в обе стороны, без бесконечного цикла
 * «скролл → setState → scrollTo → скролл».
 */
export function useSwipeablePager({
  tabCount,
  initialIndex = 0,
  onChange,
}: UseSwipeablePagerOptions): UseSwipeablePagerResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  /** Взведён, пока идёт программный скролл: его события скролла игнорируем. */
  const programmaticScroll = useRef(false);
  const settleTimer = useRef<number>();

  const commitIndex = useCallback(
    (index: number) => {
      setActiveIndex((current) => {
        if (current === index) return current;
        onChange?.(index);
        return index;
      });
    },
    [onChange],
  );

  const goTo = useCallback(
    (index: number) => {
      const container = containerRef.current;
      const clamped = Math.max(0, Math.min(index, tabCount - 1));

      commitIndex(clamped);

      if (!container) return;

      programmaticScroll.current = true;
      container.scrollTo({ left: container.clientWidth * clamped, behavior: 'smooth' });

      // scrollend поддерживается не везде (нет в Safari до 17.4), поэтому
      // снимаем флаг по таймеру — длительности smooth-скролла с запасом.
      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        programmaticScroll.current = false;
      }, 450);
    },
    [commitIndex, tabCount],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let frame = 0;

    const handleScroll = (): void => {
      if (programmaticScroll.current) return;

      // Скролл сыплет событиями на каждый кадр — считаем индекс не чаще rAF.
      if (frame) return;

      frame = requestAnimationFrame(() => {
        frame = 0;

        const width = container.clientWidth;
        if (width === 0) return;

        // Round, а не floor: переключаем вкладку на середине жеста, ровно там,
        // где scroll-snap уже решил, к какой странице прилипнуть.
        commitIndex(Math.round(container.scrollLeft / width));
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [commitIndex]);

  // Активный индекс в ref: эффект ниже не должен перезапускаться при смене
  // вкладки, иначе повторный observe() дёрнет scrollLeft посреди жеста.
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  // Поворот экрана и изменение viewport в Telegram (клавиатура, разворачивание)
  // меняют clientWidth, и позиция скролла перестаёт соответствовать индексу.
  // Пересчитываем без анимации — только когда ширина действительно изменилась.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastWidth = container.clientWidth;

    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;

      programmaticScroll.current = true;
      container.scrollLeft = width * activeIndexRef.current;

      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        programmaticScroll.current = false;
      }, 100);
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  return { activeIndex, containerRef, goTo };
}
